// setlist-sidebar — single-song promo facade sidebar.
//
// Mount target: <aside id="share-rail">. Renders #setlist-panel with one
// active .sl-song (shared song) + promo CTA. Toggles .is-open class on the
// rail aside in response to sidebarOpen state for mobile drawer animation.
//
// Desktop CSS source-of-truth: @desktop-css/setlist-panel.css (extracted
// in Phase 1). Web-only additions (.sl-promo*) live in
// apps/share-web/src/styles/setlist-sidebar.css.

import type { ShareManifest } from '@bandway/share-types';
import { subscribe } from '../state.js';
import type { AppState } from '../state.js';

const DOWNLOAD_URL = 'https://github.com/neodisa/band-way/releases/latest';

export interface SetlistSidebarHandle {
  unmount: () => void;
}

export function mountSetlistSidebar(
  host: Element,
  manifest: ShareManifest,
): SetlistSidebarHandle {
  host.textContent = '';

  const panel = document.createElement('div');
  panel.className = 'sl-panel';
  panel.id = 'setlist-panel';

  const header = document.createElement('header');
  header.className = 'sl-header';
  const title = document.createElement('span');
  title.className = 'sl-header-title';
  title.textContent = 'Setlist';
  header.appendChild(title);
  panel.appendChild(header);

  const list = document.createElement('ol');
  list.className = 'sl-list';
  list.setAttribute('role', 'list');

  const item = document.createElement('li');
  item.className = 'sl-song is-active';
  item.dataset.songId = manifest.shareId ?? '';

  const name = document.createElement('span');
  name.className = 'sl-song-name';
  name.textContent = manifest.song?.name ?? 'Untitled';
  item.appendChild(name);

  const meta = document.createElement('span');
  meta.className = 'sl-song-meta';
  const metaParts: string[] = [];
  const key = manifest.song?.key;
  const bpm = manifest.song?.bpm;
  if (key) metaParts.push(key);
  if (typeof bpm === 'number') metaParts.push(`${bpm} BPM`);
  meta.textContent = metaParts.join(' · ');
  item.appendChild(meta);

  list.appendChild(item);
  panel.appendChild(list);

  const promo = document.createElement('div');
  promo.className = 'sl-promo';
  const promoText = document.createElement('p');
  promoText.className = 'sl-promo-text';
  promoText.textContent = 'Manage setlists in the desktop app.';
  const promoCta = document.createElement('a');
  promoCta.className = 'sl-promo-cta';
  promoCta.href = DOWNLOAD_URL;
  promoCta.target = '_blank';
  promoCta.rel = 'noopener noreferrer';
  promoCta.textContent = '↓ Get app';
  promo.appendChild(promoText);
  promo.appendChild(promoCta);
  panel.appendChild(promo);

  host.appendChild(panel);

  // Drawer state reactor — subscribe fires immediately with current state,
  // so no separate initial call needed.
  function syncOpenClass(state: AppState): void {
    if (state.sidebarOpen) {
      host.classList.add('is-open');
    } else {
      host.classList.remove('is-open');
    }
  }
  const unsub = subscribe(syncOpenClass);

  return {
    unmount: () => {
      unsub();
      host.textContent = '';
      host.classList.remove('is-open');
    },
  };
}
